/**
 * Aug-2026 (User-Wunsch) — „Geteilte Aufträge"-Board: alle von mir geteilten
 * Werkstattaufträge mit Antwort-Status der Partner-Händler auf einen Blick.
 * Rendert nichts, wenn noch nie ein Auftrag geteilt wurde.
 */
import { useEffect, useState } from "react";
import { Share2, ChevronDown, ChevronUp } from "lucide-react";
import api from "../../lib/api";

const STATUS_CHIP = {
    pending: { label: "⏳ Ausstehend", cls: "bg-amber-50 text-amber-900 border-amber-300" },
    accepted: { label: "✓ Angenommen", cls: "bg-emerald-50 text-emerald-800 border-emerald-300" },
    declined: { label: "✗ Abgelehnt", cls: "bg-red-50 text-red-800 border-red-300" },
};

const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "—");

export const SharedOrdersBoard = ({ onOpenOrder }) => {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        api.get("/work-orders/shared-overview")
            .then((r) => setData(r.data))
            .catch(() => setData(null));
    }, []);

    const orders = data?.orders || [];
    if (!orders.length) return null;
    const c = data.counts || {};

    return (
        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-6">
            <div className="bg-indigo-50/60 border border-indigo-200 rounded-sm" data-testid="shared-orders-board">
                <button type="button" onClick={() => setOpen((v) => !v)}
                    className="w-full flex flex-wrap items-center gap-2 px-3 py-2.5 text-left"
                    data-testid="shared-orders-board-toggle">
                    <span className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-wider text-indigo-900">
                        <Share2 className="w-4 h-4" /> Geteilte Aufträge ({orders.length})
                    </span>
                    <span className="flex items-center gap-1.5 ml-2">
                        {c.pending > 0 && <span className={`text-[10px] font-bold border rounded-sm px-1.5 py-0.5 ${STATUS_CHIP.pending.cls}`}>{c.pending} ausstehend</span>}
                        {c.accepted > 0 && <span className={`text-[10px] font-bold border rounded-sm px-1.5 py-0.5 ${STATUS_CHIP.accepted.cls}`}>{c.accepted} angenommen</span>}
                        {c.declined > 0 && <span className={`text-[10px] font-bold border rounded-sm px-1.5 py-0.5 ${STATUS_CHIP.declined.cls}`}>{c.declined} abgelehnt</span>}
                    </span>
                    <span className="ml-auto text-indigo-700">{open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
                </button>
                {open && (
                    <div className="border-t border-indigo-200 divide-y divide-indigo-100" data-testid="shared-orders-board-list">
                        {orders.map((o) => {
                            const chip = STATUS_CHIP[o.share_status] || STATUS_CHIP.pending;
                            return (
                                <div key={o.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-xs hover:bg-indigo-50 cursor-pointer"
                                    onClick={() => onOpenOrder?.(o.id)}
                                    data-testid={`shared-order-row-${o.id}`}>
                                    <span className="font-mono-num font-bold text-[#111827]">{o.order_number}</span>
                                    <span className="text-gray-700 truncate max-w-[180px]">{o.customer_name || "—"}</span>
                                    {o.vehicle_label && <span className="text-gray-500 truncate max-w-[160px]">{o.vehicle_label}</span>}
                                    <span className="text-gray-500">→ <b className="text-indigo-900">{o.shared_with_dealer_label || o.shared_with_dealer_email}</b></span>
                                    <span className={`text-[10px] font-bold border rounded-sm px-1.5 py-0.5 ${chip.cls}`} data-testid={`shared-order-status-${o.id}`}>{chip.label}</span>
                                    <span className="text-gray-400 ml-auto">
                                        geteilt {fmtD(o.shared_at)}
                                        {o.share_responded_at ? ` · Antwort ${fmtD(o.share_responded_at)}` : ""}
                                    </span>
                                    {o.share_response_note && (
                                        <span className="basis-full text-[11px] text-gray-500 italic">„{o.share_response_note}"</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};
